import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, Plus, Minus, X, Copy } from 'lucide-react';
import { t } from '../i18n';

const apiUrl = process.env.REACT_APP_API_URL;

export default function WarehouseMovementPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    movement_type: 'in',
    movement_date: new Date().toISOString().split('T')[0],
    location_from_id: '',
    location_to_id: '',
    reference: '',
    notes: '',
    items: [
      {
        product_id: '',
        quantity: '',
        unit: '',
        unit_cost: '',
        notes: ''
      }
    ]
  });

  useEffect(() => {
    fetchProducts();
    fetchLocations();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('Потребна е најава'));
        return;
      }

      const response = await fetch(`${apiUrl}/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(Array.isArray(data) ? data : []);
      } else {
        throw new Error('Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      // Mock data for development
      setProducts([
        { id: 1, name: 'Sample Product 1', sku: 'SKU001', unit: 'pcs' },
        { id: 2, name: 'Sample Product 2', sku: 'SKU002', unit: 'kg' },
        { id: 3, name: 'Sample Product 3', sku: 'SKU003', unit: 'liters' }
      ]);
    }
  };

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const response = await fetch(`${apiUrl}/warehouse/locations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setLocations(data.data || []);
      } else {
        throw new Error('Failed to fetch locations');
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      // Mock data for development
      setLocations([
        { id: 1, code: 'A-01-01', name: 'Warehouse A - Section 1 - Rack 1' },
        { id: 2, code: 'A-01-02', name: 'Warehouse A - Section 1 - Rack 2' },
        { id: 3, code: 'B-02-03', name: 'Warehouse B - Section 2 - Rack 3' },
        { id: 4, code: 'C-03-01', name: 'Warehouse C - Section 3 - Rack 1' },
        { id: 5, code: 'DOCK-01', name: 'Loading Dock 1' }
      ]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };

    // Auto-fill unit when product is selected
    if (field === 'product_id') {
      const selectedProduct = products.find(p => p.id.toString() === value);
      if (selectedProduct) {
        newItems[index].unit = selectedProduct.unit || '';
      }
    }

    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          product_id: '',
          quantity: '',
          unit: '',
          unit_cost: '',
          notes: ''
        }
      ]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const duplicateItem = (index) => {
    const itemToDuplicate = { ...formData.items[index] };
    // Clear quantity for the duplicate
    itemToDuplicate.quantity = '';
    
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items.slice(0, index + 1),
        itemToDuplicate,
        ...prev.items.slice(index + 1)
      ]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('Потребна е најава'));
        return;
      }

      // Validate items
      const validItems = formData.items.filter(item => 
        item.product_id && item.quantity && item.unit
      );

      if (validItems.length === 0) {
        setError(t('Потребна е барем една ставка со производ, количина и единица'));
        return;
      }

      const payload = {
        ...formData,
        items: validItems
      };

      const response = await fetch(`${apiUrl}/warehouse/movement`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        setSuccess(t('Движењето {number} е успешно креирано!', { number: result.data.movement.movement_number }));
        
        // Reset form
        setFormData({
          movement_type: 'in',
          movement_date: new Date().toISOString().split('T')[0],
          location_from_id: '',
          location_to_id: '',
          reference: '',
          notes: '',
          items: [{
            product_id: '',
            quantity: '',
            unit: '',
            unit_cost: '',
            notes: ''
          }]
        });
        
        // Redirect after success
        setTimeout(() => {
          navigate('/warehouse/list');
        }, 2000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || t('Неуспешно креирање на движење'));
      }
    } catch (error) {
      console.error('Error creating movement:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const movementTypes = [
    { value: 'in', label: t('Влез'), icon: Plus, description: t('Додади залиха во магацинот') },
    { value: 'out', label: t('Излез'), icon: Minus, description: t('Извади залиха од магацинот') },
    { value: 'transfer', label: t('Пренос'), icon: ArrowUpDown, description: t('Премести меѓу локации') }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <ArrowUpDown className="text-orange-600" size={32} />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('Ново движење во магацин')}</h1>
              <p className="text-gray-600">{t('Додајте, извадете или пренесете залиха за повеќе производи')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/warehouse/list')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
            >
              {t('← Назад кон магацин')}
            </button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Movement Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">{t('Тип на движење')}</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {movementTypes.map((type) => {
                const IconComponent = type.icon;
                return (
                  <div
                    key={type.value}
                    className={`relative border rounded-lg p-4 cursor-pointer transition-all ${
                      formData.movement_type === type.value
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, movement_type: type.value }))}
                  >
                    <input
                      type="radio"
                      name="movement_type"
                      value={type.value}
                      checked={formData.movement_type === type.value}
                      onChange={handleInputChange}
                      className="absolute top-4 right-4"
                    />
                    <div className="flex items-center gap-3">
                      <IconComponent size={24} className="text-orange-600" />
                      <div>
                        <h3 className="font-medium text-gray-900">{type.label}</h3>
                        <p className="text-sm text-gray-500">{type.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Movement Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="movement_date" className="block text-sm font-medium text-gray-700 mb-2">
                {t('Датум на движење *')}
              </label>
              <input
                type="date"
                id="movement_date"
                name="movement_date"
                value={formData.movement_date}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Location From (for transfer/out) */}
            {(formData.movement_type === 'transfer' || formData.movement_type === 'out') && (
              <div>
                <label htmlFor="location_from_id" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Од локација *')}
                </label>
                <select
                  id="location_from_id"
                  name="location_from_id"
                  value={formData.location_from_id}
                  onChange={handleInputChange}
                  required={formData.movement_type === 'transfer' || formData.movement_type === 'out'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">{t('Изберете локација')}</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.code} - {location.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Location To (for transfer/in) */}
            {(formData.movement_type === 'transfer' || formData.movement_type === 'in') && (
              <div>
                <label htmlFor="location_to_id" className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.movement_type === 'transfer' ? t('До локација *') : t('Локација *')}
                </label>
                <select
                  id="location_to_id"
                  name="location_to_id"
                  value={formData.location_to_id}
                  onChange={handleInputChange}
                  required={formData.movement_type === 'transfer' || formData.movement_type === 'in'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">{t('Изберете локација')}</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.code} - {location.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Reference - only for 'out' movements */}
            {formData.movement_type === 'out' && (
              <div>
                <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('Референца')}
                </label>
                <input
                  type="text"
                  id="reference"
                  name="reference"
                  value={formData.reference}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder={t('Бр. на фактура, нарачка и сл.')}
                />
              </div>
            )}
          </div>

          {/* Items Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('Ставки')}</h3>
              <button
                type="button"
                onClick={addItem}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <Plus size={16} />
                {t('Додади ставка')}
              </button>
            </div>

            <div className="space-y-4">
              {formData.items.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                  {/* Item Actions */}
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => duplicateItem(index)}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title={t('Дуплирај ставка')}
                    >
                      <Copy size={16} />
                    </button>
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title={t('Отстрани ставка')}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    {/* Product Selection */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('Производ *')}
                      </label>
                      <select
                        value={item.product_id}
                        onChange={(e) => handleItemChange(index, 'product_id', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">{t('Изберете производ')}</option>
                        {products.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name} ({product.sku})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Location, Quantity, Cost, Price in same line */}
                    <div className="md:col-span-2">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {/* Quantity */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('Количина *')}
                          </label>
                          <div className="flex">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              required
                              min="0"
                              step="0.01"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                              placeholder="0.00"
                            />
                            <input
                              type="text"
                              value={item.unit}
                              onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                              className="w-16 px-2 py-2 border border-l-0 border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                              placeholder={t('ед.')}
                            />
                          </div>
                        </div>

                        {/* Unit Cost */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('Единечна набавна цена')}
                          </label>
                          <input
                            type="number"
                            value={item.unit_cost}
                            onChange={(e) => handleItemChange(index, 'unit_cost', e.target.value)}
                            min="0"
                            step="0.01"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="0.00"
                          />
                        </div>

                        {/* Total Price (calculated) */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('Вкупна цена')}
                          </label>
                          <input
                            type="text"
                            value={item.quantity && item.unit_cost ? (parseFloat(item.quantity) * parseFloat(item.unit_cost)).toFixed(2) : ''}
                            readOnly
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* General Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              {t('Општи забелешки')}
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder={t('Општи забелешки за ова движење...')}
            />
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-md">
              {success}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {t('Се креира...')}
                </>
              ) : (
                <>
                  <ArrowUpDown size={16} />
                  {t('Креирај движење ({count} ставки)', { count: formData.items.filter(item => item.product_id && item.quantity).length })}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/warehouse/list')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {t('Откажи')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}